import React from "react";
import {
  ClipboardCheck, FileText, ShieldCheck, MessageSquare, Inbox,
  ScrollText, Sparkles, Send, Users,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ParallelGrid, FeatureCallout, AgentCard,
  TimelineList, FlowDiagram, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogMorganZeroTouchIntake = () => (
  <RichBlogLayout
    category="Patient Experience"
    eyebrow="Patient Intake"
    title="Morgan: Every New Patient Arrives 100% Intaked — Without Anyone Lifting a Finger"
    subtitle="Forms chased automatically, insurance cards OCR'd, structured fields written straight into the chart. The most-hated job at the front desk, gone."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        It's Tuesday morning, 8:42 AM. The first new patient of the day —
        Maria, a referral for a hygiene visit — is checking in at the front
        desk. She's holding her phone, squinting at it. The intake form she
        was sent yesterday has eight pages. She made it through three.
      </p>
      <p>
        Behind the counter, the front desk hands her a clipboard. The same
        eight pages, this time on paper. The hygienist is ready in op three.
        The schedule is already starting to slip. By the time Maria's
        insurance card is photographed, retyped, and pasted into the PMS,
        the next patient has been waiting in the lobby for fourteen minutes.
      </p>
      <p>
        Patient intake is the single most-hated job at most front desks — and
        it shouldn't be a human job at all. Repetitive, easily interrupted,
        unforgiving of mistakes. And it's the first impression every new
        patient has of your practice.
      </p>
    </Prose>

    <AgentCard
      name="Morgan"
      role="AI Patient Intake · Forms + Insurance + Chart"
      initial="M"
      description="Morgan makes sure every new patient on tomorrow's schedule arrives fully prepared — forms completed, insurance card processed, structured data written cleanly into the PMS. She runs every intake at the same time, chases what's missing, and never asks a teammate to step in unless something genuinely needs a human."
      channels={["SMS chase", "Smart forms", "Insurance OCR", "PMS write-back"]}
    />

    <Prose>
      <h2>What intake actually costs the front desk</h2>
      <p>
        Take a practice that books 8–12 new patients a week. Each one needs
        a form chase, an insurance card photo, a benefits scan, and clean
        data entered into the PMS. Conservatively, that's 15–25 minutes of
        front-desk time per new patient, plus interruptions every time
        someone calls or walks in.
      </p>
      <p>
        Across a month, that's a part-time job no one was hired to do.
        Across a year, it's a slow drip of frustration that shows up as
        higher turnover at the front desk and lower star ratings online.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 100, suffix: "%", label: "New patients fully intaked" },
        { value: 0, label: "Staff minutes per intake" },
        { value: 8, label: "Form chases per week, automated" },
        { value: 24, suffix: "/7", label: "Always on, never off shift" },
      ]}
    />

    <Prose>
      <p>
        Morgan does not add another person to the intake process. She removes
        the process from the people. Every new patient is on her own
        ongoing track — chased gently, reminded thoughtfully, and finished
        cleanly.
      </p>

      <h2>Most digital intake stops at the form</h2>
      <p>
        Most "digital intake" products do one of two things: they send a
        link, or they OCR a photo. Morgan does the entire job — she gets
        the form back, she reads the insurance card, she normalizes the
        fields, and she writes them into the chart in the same shape your
        team expects. If the photo is blurry, she asks for another one. If
        the patient stops halfway through, she follows up.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="The traditional intake"
      afterTitle="With Morgan"
      beforeItems={[
        "Form sent once, then forgotten if the patient doesn't fill it out",
        "Insurance card photo arrives blurry or rotated, retyped manually",
        "Front desk re-asks the patient at check-in, schedule slips",
        "Same data lives in three places — paper, scan, PMS — none in sync",
      ]}
      afterItems={[
        "Forms chased intelligently across SMS until they're complete",
        "Insurance cards OCR'd, member ID and group number normalized",
        "Patient walks in with everything done — chart already populated",
        "Clean handoff to Amanda for procedure-level verification",
      ]}
    />

    <Prose>
      <h2>The full intake workflow, automated end to end</h2>
      <p>
        Morgan starts the moment a new patient is added to the schedule.
        From there, she runs the entire intake workflow — politely,
        persistently, and without anyone on the team touching it.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Sends the right forms, in the right order",
          body:
            "As soon as a new patient is booked, Morgan dispatches a mobile-first intake link tailored to the visit type — chief complaint, medical history, consents, insurance upload.",
        },
        {
          title: "Chases until everything is complete",
          body:
            "If the patient stops halfway, Morgan nudges by text the next day. If a section is skipped, she circles back to just that section — never sending the whole form again.",
        },
        {
          title: "OCRs the insurance card image",
          body:
            "Member ID, group number, plan name, and effective dates are extracted from the front and back. Bad photos are detected and a re-upload is requested automatically.",
        },
        {
          title: "Normalizes the data into your PMS schema",
          body:
            "Fields are mapped to the exact shape your PMS expects — no dangling text blobs, no free-text 'see attachment' fields, no manual cleanup.",
        },
        {
          title: "Writes everything directly into the chart",
          body:
            "Demographics, medical history, allergies, insurance — all populated on the patient record before anyone on your team opens it.",
        },
        {
          title: "Hands off cleanly to Amanda for verification",
          body:
            "The moment intake is complete, Morgan triggers Amanda to begin procedure-level eligibility on the captured insurance information. No waiting, no manual baton pass.",
        },
      ]}
    />

    <PullQuote>
      The hardest part of intake isn't the form. It's the chasing — the four
      reminders, the blurry card photo, the squinting at a scrawled member ID.
      Morgan does that part.
    </PullQuote>

    <Prose>
      <h2>Eight new patients in a single week, all at once</h2>
      <p>
        On a normal week, eight new patients land on the schedule. In the
        old model, that's eight separate form chases, eight insurance
        cards, eight rounds of retyping — spread across whoever happens to
        be free between phone calls. In the Morgan model, it's one process
        running all eight tracks concurrently.
      </p>
      <p>
        Each row below represents an intake at a different stage. Morgan
        is working every one of them at the same time — without your front
        desk touching a single field.
      </p>
    </Prose>

    <ParallelGrid
      title="Morgan, mid-shift — eight intakes happening at once"
      columns={2}
      items={[
        { title: "Maria L.", subtitle: "Forms 100% · ID + group extracted", icon: ClipboardCheck, status: "done" },
        { title: "James T.", subtitle: "Awaiting back-of-card photo", icon: FileText, status: "active" },
        { title: "Anna P.", subtitle: "Medical history complete", icon: ScrollText, status: "done" },
        { title: "David R.", subtitle: "Reminder #2 sent · 24h", icon: MessageSquare, status: "active" },
        { title: "Priya S.", subtitle: "Insurance card OCR'd · Aetna", icon: ShieldCheck, status: "done" },
        { title: "Linda K.", subtitle: "Consent forms signed", icon: ClipboardCheck, status: "done" },
        { title: "Tom B.", subtitle: "Form started, halfway through", icon: FileText, status: "active" },
        { title: "Sofia G.", subtitle: "Handed off to Amanda", icon: Sparkles, status: "done" },
      ]}
    />

    <Prose>
      <h2>The intake-to-chair timeline</h2>
      <p>
        Here's how a single new patient — Maria — moves through Morgan's
        workflow without anyone on your team touching it. The booking
        happens in the morning. By that evening, every form is complete,
        the insurance card is captured, and the chart is fully populated
        for the hygienist to review.
      </p>
    </Prose>

    <TimelineList
      title="Patient timeline · Maria L. · new patient intake"
      events={[
        { time: "Mon 9:14 AM", channel: "call", label: "Amy booked appointment", by: "Asha · 4 min call" },
        { time: "Mon 9:15 AM", channel: "sms", label: "Morgan dispatched intake link", by: "Mobile-first form" },
        { time: "Mon 12:32 PM", channel: "form", label: "Medical history submitted", by: "Patient · in-app" },
        { time: "Mon 6:48 PM", channel: "sms", label: "Reminder for insurance card", by: "Morgan" },
        { time: "Mon 7:22 PM", channel: "form", label: "Insurance card uploaded · Delta", by: "Patient" },
        { time: "Mon 7:22 PM", channel: "form", label: "OCR complete · ID + group extracted", by: "Morgan" },
        { time: "Mon 7:23 PM", channel: "form", label: "Chart fields written to PMS", by: "Morgan" },
        { time: "Mon 7:24 PM", channel: "form", label: "Handed off to Amanda for verification", by: "Morgan → Amanda" },
      ]}
    />

    <Prose>
      <h2>The handoff that makes this matter</h2>
      <p>
        Most intake tools stop at the form. Morgan stops only when the
        chart is whole. The moment the insurance fields are clean, she
        triggers Amanda — the procedure-level verification agent — to
        begin running benefits, frequency limits, and waiting periods.
      </p>
      <p>
        That handoff is the difference between intake-as-a-chore and
        intake-as-a-pipeline. By the time the patient walks in, the chart
        is not just filled in — it's already been verified against the
        carrier.
      </p>
    </Prose>

    <FlowDiagram
      title="From booked appointment to chair-ready chart"
      nodes={[
        { label: "New patient booked", sublabel: "Schedule update", icon: Users },
        { label: "Morgan dispatches forms", sublabel: "Smart, mobile-first", icon: Send },
        { label: "Insurance OCR + chart write", sublabel: "Clean, structured", icon: Inbox },
        { label: "Handoff to Amanda", sublabel: "Verification begins", icon: ShieldCheck },
      ]}
    />

    <FeatureCallout
      stat="0 minutes"
      label="Of staff time per new-patient intake"
      description="Morgan handles every form chase, every photo retake, every chart write — and only loops in your team if there's something only a human can decide."
      icon={ClipboardCheck}
    />

    <Prose>
      <h2>What's left for the human team</h2>
      <p>
        The front desk doesn't lose a job here — they lose the worst part
        of it. The chasing, the retyping, the squinting at blurry phone
        photos. What's left is the human work: greeting Maria warmly,
        walking her back to op three, and handing her off to a hygienist
        who already knows her medical history.
      </p>
      <p>
        For the patient, the experience flips entirely. There is no
        clipboard. There is no "can you fill this out one more time." There
        is just a warm greeting and a chair that's ready.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Patient intake should never have been a human job. Morgan turns it
        into something that quietly takes care of itself off the front desk —
        ending the moment the chart is whole and the patient is ready to be
        seen.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogMorganZeroTouchIntake;
